import React, { useState, useEffect, forwardRef, useCallback } from 'react'
import { set } from 'sanity'
import { useClient } from 'sanity'
import { ObjectInputProps } from 'sanity'
import {
  Card,
  Stack,
  Box,
  TextInput,
  Heading,
  Text,
  Flex,
  Grid,
  Label,
  Checkbox,
  Select,
  Radio,
} from '@sanity/ui'

interface TireSize {
  _id: string
  defaultTireFormat: 'standard' | 'slick' | 'offroad'
  treadWidthMetric?: number
  aspectRatio?: number
  tireDiameterMetric?: number
  tireDiameterImperial?: number
  treadWidthImperial?: number
  wheelDiameter?: number
  wheelDiameterId?: string
}

interface WheelDiameter {
  _id: string
  wheelDiameter: number
}

interface SelectedSize {
  _key: string
  tireSize?: {
    _ref: string
    _type: 'reference'
  }
  sizeRating?: string
  loadIndex?: number
  speedIndex?: string
  loadRange?: string
  maxLoad?: number
  maxInflationPressure?: number
  treadDepth?: string
  tireWeight?: number
  rimWidthRange?: string
  measRimWidth?: number
  sectWidth?: number
  treadWidth?: number
  overallDiam?: number
  revsPerMile?: number
  countryOfOrigin?: string
  sourceUrl?: string
  internalNotes?: string
}

interface TireSizesSelectorValue {
  defaultTireFormat: 'standard' | 'slick' | 'offroad'
  sizes: SelectedSize[]
}

const createKey = () => `${Date.now()}${Math.random()}`

// Repairs size entries before they are edited (APE-59):
// - Sizes created by the tire data import have no _key. Edits are matched by
//   _key, so every keyless size matched `undefined` and one edit changed them all.
// - treadDepth is a string ("10.5/32"), but this input used to save numbers,
//   which fail schema validation and block publishing. Positive numbers become
//   "n/32". 0 is left as is: it marks a depth that scripts/fix-tire-tread-depth.ts
//   restores from the published document, and clearing it here would lose that.
const normalizeSizes = (sizes: SelectedSize[] = []): SelectedSize[] =>
  sizes.map((size) => {
    const treadDepth: unknown = size.treadDepth
    return {
      ...size,
      _key: size._key || createKey(),
      treadDepth:
        typeof treadDepth === 'number' && treadDepth > 0 ? `${treadDepth}/32` : size.treadDepth,
    }
  })

const TIRE_FORMAT_OPTIONS = [
  { title: 'Standard', value: 'standard' as const },
  { title: 'Racing Slick', value: 'slick' as const },
  { title: 'Off-road', value: 'offroad' as const },
]

const TireSizesSelector = forwardRef<HTMLDivElement, ObjectInputProps>((props, ref) => {
  const { schemaType, value, onChange } = props

  const typedValue = value as TireSizesSelectorValue | undefined

  const [tireSizes, setTireSizes] = useState<TireSize[]>([])
  const [wheelDiameters, setWheelDiameters] = useState<WheelDiameter[]>([])
  const [selectedSizes, setSelectedSizes] = useState<SelectedSize[]>(
    normalizeSizes(typedValue?.sizes),
  )
  const [defaultTireFormat, setDefaultTireFormat] = useState<
    'standard' | 'slick' | 'offroad'
  >(typedValue?.defaultTireFormat || 'standard')
  const [searchWidthMetric, setSearchWidthMetric] = useState('')
  const [searchAspectRatio, setSearchAspectRatio] = useState('')
  const [searchWheelDiameter, setSearchWheelDiameter] = useState('')
  const [searchTireDiameterMetric, setSearchTireDiameterMetric] = useState('')
  const [searchTireDiameterImperial, setSearchTireDiameterImperial] = useState('')
  const [searchTreadWidthImperial, setSearchTreadWidthImperial] = useState('')

  const client = useClient({ apiVersion: '2023-05-03' })

  useEffect(() => {
    let mounted = true

    const fetchData = async () => {
      try {
        // Fetch tire sizes
        const tireSizesQuery = `*[_type == "tireSize"] {
          _id,
          defaultTireFormat,
          treadWidthMetric,
          aspectRatio,
          tireDiameterMetric,
          tireDiameterImperial,
          treadWidthImperial,
          "wheelDiameter": diameter->wheelDiameter,
          "wheelDiameterId": diameter->_id
        } | order(defaultTireFormat asc, treadWidthMetric asc)`

        // Fetch wheel diameters
        const wheelDiametersQuery = `*[_type == "diameter"] | order(wheelDiameter asc) {
          _id,
          wheelDiameter
        }`

        const [sizes, diameters] = await Promise.all([
          client.fetch<TireSize[]>(tireSizesQuery),
          client.fetch<WheelDiameter[]>(wheelDiametersQuery),
        ])

        if (mounted) {
          setTireSizes(sizes)
          setWheelDiameters(diameters)
        }
      } catch (err) {
        console.error('Error fetching data:', err)
      }
    }

    fetchData()

    return () => {
      mounted = false
    }
  }, [client])

  useEffect(() => {
    if (typedValue) {
      setSelectedSizes(normalizeSizes(typedValue.sizes))
      setDefaultTireFormat(typedValue.defaultTireFormat || 'standard')
    }
  }, [typedValue])

  const formatTireSize = useCallback((size: TireSize) => {
    if (!size) return 'Unknown Size'

    switch (size.defaultTireFormat) {
      case 'standard':
        return `${size.treadWidthMetric}/${size.aspectRatio}-${size.wheelDiameter}`
      case 'slick':
        return `${size.treadWidthMetric}/${size.tireDiameterMetric}-${size.wheelDiameter}`
      case 'offroad':
        return `${size.tireDiameterImperial}x${size.treadWidthImperial}R${size.wheelDiameter}`
      default:
        return 'Invalid Format'
    }
  }, [])

  const handleTireFormatChange = useCallback(
    (format: 'standard' | 'slick' | 'offroad') => {
      const newValue: TireSizesSelectorValue = {
        defaultTireFormat: format,
        sizes: selectedSizes,
      }
      setDefaultTireFormat(format)
      onChange(set(newValue))
    },
    [selectedSizes, onChange],
  )

  const handleSizeToggle = useCallback(
    (tireSize: TireSize) => {
      const existingIndex = selectedSizes.findIndex(
        (size) => size.tireSize?._ref === tireSize._id,
      )

      let newSizes = [...selectedSizes]
      if (existingIndex >= 0) {
        newSizes = newSizes.filter((_, index) => index !== existingIndex)
      } else {
        newSizes.push({
          _key: createKey(),
          tireSize: { _ref: tireSize._id, _type: 'reference' },
        })
      }

      setSelectedSizes(newSizes)
      onChange(set({ defaultTireFormat, sizes: newSizes }))
    },
    [selectedSizes, defaultTireFormat, onChange],
  )

  const handleFieldChange = useCallback(
    (sizeKey: string, fieldName: string, value: string, isNumeric = false) => {
      const newSizes = selectedSizes.map((size) => {
        if (size._key === sizeKey) {
          let processedValue: any = value
          if (isNumeric && value !== '') {
            const numValue = parseFloat(value)
            processedValue = !isNaN(numValue) ? numValue : undefined
          } else if (value === '') {
            processedValue = undefined
          }
          return {
            ...size,
            [fieldName]: processedValue,
          }
        }
        return size
      })
      setSelectedSizes(newSizes)
      onChange(set({ defaultTireFormat, sizes: newSizes }))
    },
    [selectedSizes, defaultTireFormat, onChange],
  )

  const filteredTireSizes = tireSizes.filter((size) => {
    const widthMatch = searchWidthMetric
      ? size.treadWidthMetric?.toString().includes(searchWidthMetric)
      : true
    const aspectMatch = searchAspectRatio
      ? size.aspectRatio?.toString().includes(searchAspectRatio)
      : true
    const diameterMatch = searchWheelDiameter
      ? size.wheelDiameterId === searchWheelDiameter
      : true
    const tireFormatMatch = size.defaultTireFormat === defaultTireFormat
    const tireDiameterMetricMatch = searchTireDiameterMetric
      ? size.tireDiameterMetric?.toString().includes(searchTireDiameterMetric)
      : true
    const tireDiameterImperialMatch = searchTireDiameterImperial
      ? size.tireDiameterImperial?.toString().includes(searchTireDiameterImperial)
      : true
    const treadWidthImperialMatch = searchTreadWidthImperial
      ? size.treadWidthImperial?.toString().includes(searchTreadWidthImperial)
      : true

    return (
      widthMatch &&
      aspectMatch &&
      diameterMatch &&
      tireFormatMatch &&
      tireDiameterMetricMatch &&
      tireDiameterImperialMatch &&
      treadWidthImperialMatch
    )
  })

  return (
    <Stack gap={3} ref={ref}>
      <Box>
        <Label size={1}>{schemaType.title}</Label>
        {schemaType.description && (
          <Box paddingTop={2}>
            <Label size={0} muted>
              {schemaType.description}
            </Label>
          </Box>
        )}
      </Box>

      <Stack gap={4}>
        <Card padding={3} radius={2} shadow={1}>
          <Stack gap={3}>
            <Heading as="h6" size={0}>
              Tire Format
            </Heading>
            <Flex gap={3}>
              {TIRE_FORMAT_OPTIONS.map((option) => (
                <Flex key={option.value} align="center" gap={2}>
                  <Radio
                    checked={defaultTireFormat === option.value}
                    name="tireFormat"
                    onChange={() => handleTireFormatChange(option.value)}
                  />
                  <Text>{option.title}</Text>
                </Flex>
              ))}
            </Flex>
          </Stack>
        </Card>

        <Card padding={3} radius={2} shadow={1}>
          <Stack gap={3}>
            <Heading as="h6" size={0}>
              Search Tire Sizes
            </Heading>
            <Grid gridTemplateColumns={3} gap={3}>
              {defaultTireFormat === 'standard' && (
                <>
                  <Stack gap={2}>
                    <Heading size={0}>Tread Width (mm)</Heading>
                    <TextInput
                      value={searchWidthMetric}
                      onChange={(event) => setSearchWidthMetric(event.target.value)}
                    />
                  </Stack>
                  <Stack gap={2}>
                    <Heading size={0}>Aspect Ratio</Heading>
                    <TextInput
                      value={searchAspectRatio}
                      onChange={(event) => setSearchAspectRatio(event.target.value)}
                    />
                  </Stack>
                </>
              )}

              {defaultTireFormat === 'slick' && (
                <>
                  <Stack gap={2}>
                    <Heading size={0}>Tread Width (mm)</Heading>
                    <TextInput
                      value={searchWidthMetric}
                      onChange={(event) => setSearchWidthMetric(event.target.value)}
                    />
                  </Stack>
                  <Stack gap={2}>
                    <Heading size={0}>Tire Diameter (mm)</Heading>
                    <TextInput
                      value={searchTireDiameterMetric}
                      onChange={(event) =>
                        setSearchTireDiameterMetric(event.target.value)
                      }
                    />
                  </Stack>
                </>
              )}

              {defaultTireFormat === 'offroad' && (
                <>
                  <Stack gap={2}>
                    <Heading size={0}>Tire Diameter (inch)</Heading>
                    <TextInput
                      value={searchTireDiameterImperial}
                      onChange={(event) =>
                        setSearchTireDiameterImperial(event.target.value)
                      }
                    />
                  </Stack>
                  <Stack gap={2}>
                    <Heading size={0}>Tread Width (inch)</Heading>
                    <TextInput
                      value={searchTreadWidthImperial}
                      onChange={(event) =>
                        setSearchTreadWidthImperial(event.target.value)
                      }
                    />
                  </Stack>
                </>
              )}

              <Stack gap={2}>
                <Heading size={0}>Wheel Diameter</Heading>
                <Select
                  value={searchWheelDiameter}
                  onChange={(event) => setSearchWheelDiameter(event.currentTarget.value)}
                >
                  <option value="">All diameters</option>
                  {wheelDiameters.map((diameter) => (
                    <option key={diameter._id} value={diameter._id}>
                      {diameter.wheelDiameter}"
                    </option>
                  ))}
                </Select>
              </Stack>
            </Grid>
          </Stack>
        </Card>

        <Card padding={3} radius={2} shadow={1}>
          <Stack gap={3}>
            <Flex align="center" justify="space-between">
              <Heading as="h6" size={0}>
                Available Tire Sizes
              </Heading>
              {selectedSizes.length > 0 && (
                <Text size={1} weight="semibold" tone="primary">
                  {selectedSizes.length} selected
                </Text>
              )}
            </Flex>

            <Box style={{ maxHeight: '350px', overflow: 'auto' }}>
              {filteredTireSizes.length > 0 ? (
                <Grid gridTemplateColumns={3} gap={3}>
                  {filteredTireSizes.map((tireSize) => {
                    const isSelected = selectedSizes.some(
                      (size) => size.tireSize?._ref === tireSize._id,
                    )
                    return (
                      <Card
                        key={tireSize._id}
                        padding={2}
                        radius={2}
                        tone={isSelected ? 'primary' : 'default'}
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleSizeToggle(tireSize)}
                      >
                        <Flex align="center" gap={2}>
                          <Checkbox
                            checked={isSelected}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleSizeToggle(tireSize)
                            }}
                          />
                          <Stack gap={1}>
                            <Text size={1} weight="semibold">
                              {formatTireSize(tireSize)}
                            </Text>
                            <Text size={0} muted>
                              {tireSize.defaultTireFormat}
                            </Text>
                          </Stack>
                        </Flex>
                      </Card>
                    )
                  })}
                </Grid>
              ) : (
                <Text>No tire sizes match your search criteria</Text>
              )}
            </Box>
          </Stack>
        </Card>

        {selectedSizes.length > 0 && (
          <Card padding={3} radius={2} shadow={1}>
            <Stack gap={3}>
              <Flex align="center" justify="space-between">
                <Heading as="h6" size={0}>
                  Selected Tire Sizes & Specifications
                </Heading>
                <Text size={1} weight="semibold" tone="positive">
                  {selectedSizes.length}{' '}
                  {selectedSizes.length === 1 ? 'size' : 'sizes'} configured
                </Text>
              </Flex>
              <Stack gap={4}>
                {selectedSizes.map((selectedSize) => {
                  const tireSize = tireSizes.find(
                    (t) => t._id === selectedSize.tireSize?._ref,
                  )
                  if (!tireSize) return null

                  return (
                    <Card
                      key={selectedSize._key}
                      padding={3}
                      radius={2}
                      shadow={1}
                      tone="default"
                    >
                      <Stack gap={4}>
                        <Stack gap={2}>
                          <Text size={2} weight="semibold">
                            {formatTireSize(tireSize)}
                          </Text>
                          <Text size={1} muted>
                            {tireSize.defaultTireFormat}
                          </Text>
                        </Stack>

                        {/* Load & Speed Ratings */}
                        <Stack gap={3}>
                          <Heading as="h6" size={0}>
                            Load & Speed Ratings
                          </Heading>
                          <Grid gridTemplateColumns={3} gap={3}>
                            <Stack gap={2}>
                              <Label size={1}>Size Rating (tire code)</Label>
                              <TextInput
                                value={selectedSize.sizeRating ?? ''}
                                onChange={(event) =>
                                  handleFieldChange(
                                    selectedSize._key,
                                    'sizeRating',
                                    event.target.value,
                                  )
                                }
                              />
                            </Stack>
                            <Stack gap={2}>
                              <Label size={1}>Load Index</Label>
                              <TextInput
                                type="number"
                                value={selectedSize.loadIndex ?? ''}
                                onChange={(event) =>
                                  handleFieldChange(
                                    selectedSize._key,
                                    'loadIndex',
                                    event.target.value,
                                    true,
                                  )
                                }
                              />
                            </Stack>
                            <Stack gap={2}>
                              <Label size={1}>Speed Index</Label>
                              <TextInput
                                value={selectedSize.speedIndex ?? ''}
                                onChange={(event) =>
                                  handleFieldChange(
                                    selectedSize._key,
                                    'speedIndex',
                                    event.target.value,
                                  )
                                }
                                placeholder="e.g., H, V, W, Y"
                              />
                            </Stack>
                            <Stack gap={2}>
                              <Label size={1}>Load Range</Label>
                              <TextInput
                                value={selectedSize.loadRange ?? ''}
                                onChange={(event) =>
                                  handleFieldChange(
                                    selectedSize._key,
                                    'loadRange',
                                    event.target.value,
                                  )
                                }
                                placeholder="e.g., SL, XL, E"
                              />
                            </Stack>
                            <Stack gap={2}>
                              <Label size={1}>Max Load (lbs)</Label>
                              <TextInput
                                type="number"
                                value={selectedSize.maxLoad ?? ''}
                                onChange={(event) =>
                                  handleFieldChange(
                                    selectedSize._key,
                                    'maxLoad',
                                    event.target.value,
                                    true,
                                  )
                                }
                              />
                            </Stack>
                            <Stack gap={2}>
                              <Label size={1}>Max Inflation (PSI)</Label>
                              <TextInput
                                type="number"
                                value={selectedSize.maxInflationPressure ?? ''}
                                onChange={(event) =>
                                  handleFieldChange(
                                    selectedSize._key,
                                    'maxInflationPressure',
                                    event.target.value,
                                    true,
                                  )
                                }
                              />
                            </Stack>
                          </Grid>
                        </Stack>

                        {/* Physical Dimensions */}
                        <Stack gap={3}>
                          <Heading as="h6" size={0}>
                            Physical Dimensions
                          </Heading>
                          <Grid gridTemplateColumns={3} gap={3}>
                            <Stack gap={2}>
                              <Label size={1}>Tread Depth (32nds)</Label>
                              <TextInput
                                value={selectedSize.treadDepth ?? ''}
                                onChange={(event) =>
                                  handleFieldChange(
                                    selectedSize._key,
                                    'treadDepth',
                                    event.target.value,
                                  )
                                }
                                placeholder="e.g., 10/32"
                              />
                            </Stack>
                            <Stack gap={2}>
                              <Label size={1}>Tire Weight (lbs)</Label>
                              <TextInput
                                type="number"
                                value={selectedSize.tireWeight ?? ''}
                                onChange={(event) =>
                                  handleFieldChange(
                                    selectedSize._key,
                                    'tireWeight',
                                    event.target.value,
                                    true,
                                  )
                                }
                              />
                            </Stack>
                            <Stack gap={2}>
                              <Label size={1}>Rim Width Range</Label>
                              <TextInput
                                value={selectedSize.rimWidthRange ?? ''}
                                onChange={(event) =>
                                  handleFieldChange(
                                    selectedSize._key,
                                    'rimWidthRange',
                                    event.target.value,
                                  )
                                }
                                placeholder="e.g., 7.0-8.5"
                              />
                            </Stack>
                            <Stack gap={2}>
                              <Label size={1}>Measuring Rim Width</Label>
                              <TextInput
                                type="number"
                                step="0.5"
                                value={selectedSize.measRimWidth ?? ''}
                                onChange={(event) =>
                                  handleFieldChange(
                                    selectedSize._key,
                                    'measRimWidth',
                                    event.target.value,
                                    true,
                                  )
                                }
                              />
                            </Stack>
                            <Stack gap={2}>
                              <Label size={1}>Section Width (mm)</Label>
                              <TextInput
                                type="number"
                                value={selectedSize.sectWidth ?? ''}
                                onChange={(event) =>
                                  handleFieldChange(
                                    selectedSize._key,
                                    'sectWidth',
                                    event.target.value,
                                    true,
                                  )
                                }
                              />
                            </Stack>
                            <Stack gap={2}>
                              <Label size={1}>Tread Width (mm)</Label>
                              <TextInput
                                type="number"
                                value={selectedSize.treadWidth ?? ''}
                                onChange={(event) =>
                                  handleFieldChange(
                                    selectedSize._key,
                                    'treadWidth',
                                    event.target.value,
                                    true,
                                  )
                                }
                              />
                            </Stack>
                            <Stack gap={2}>
                              <Label size={1}>Overall Diameter (inches)</Label>
                              <TextInput
                                type="number"
                                step="0.1"
                                value={selectedSize.overallDiam ?? ''}
                                onChange={(event) =>
                                  handleFieldChange(
                                    selectedSize._key,
                                    'overallDiam',
                                    event.target.value,
                                    true,
                                  )
                                }
                              />
                            </Stack>
                            <Stack gap={2}>
                              <Label size={1}>Revolutions Per Mile</Label>
                              <TextInput
                                type="number"
                                value={selectedSize.revsPerMile ?? ''}
                                onChange={(event) =>
                                  handleFieldChange(
                                    selectedSize._key,
                                    'revsPerMile',
                                    event.target.value,
                                    true,
                                  )
                                }
                              />
                            </Stack>
                          </Grid>
                        </Stack>

                        {/* Documentation */}
                        <Stack gap={3}>
                          <Heading as="h6" size={0}>
                            Documentation
                          </Heading>
                          <Grid gridTemplateColumns={2} gap={3}>
                            <Stack gap={2}>
                              <Label size={1}>Country of Origin</Label>
                              <TextInput
                                value={selectedSize.countryOfOrigin ?? ''}
                                onChange={(event) =>
                                  handleFieldChange(
                                    selectedSize._key,
                                    'countryOfOrigin',
                                    event.target.value,
                                  )
                                }
                              />
                            </Stack>
                            <Stack gap={2}>
                              <Label size={1}>Source URL</Label>
                              <TextInput
                                type="url"
                                value={selectedSize.sourceUrl ?? ''}
                                onChange={(event) =>
                                  handleFieldChange(
                                    selectedSize._key,
                                    'sourceUrl',
                                    event.target.value,
                                  )
                                }
                                placeholder="https://..."
                              />
                            </Stack>
                          </Grid>
                          <Stack gap={2}>
                            <Label size={1}>Internal Notes</Label>
                            <TextInput
                              value={selectedSize.internalNotes ?? ''}
                              onChange={(event) =>
                                handleFieldChange(
                                  selectedSize._key,
                                  'internalNotes',
                                  event.target.value,
                                )
                              }
                            />
                          </Stack>
                        </Stack>
                      </Stack>
                    </Card>
                  )
                })}
              </Stack>
            </Stack>
          </Card>
        )}
      </Stack>
    </Stack>
  )
})

TireSizesSelector.displayName = 'TireSizesSelector'

export default TireSizesSelector
